// useProofTypes — the ACTIVE proof types (system_config `proof_type`), for the Mark paid form and for
// labelling a payment's proof. The server re-checks the type, so this only decides what an admin is offered.
// A short shared cache keeps a ledger of many rows from fetching the list once per row; the Mark paid
// form asks for `fresh` so a type added a moment ago in System Config appears straight away.
import { useEffect, useState } from "react";
import { getJson } from "./useApi.js";
import type { SystemConfig } from "@dm-lms/shared/types";

export interface ProofTypeOption {
  key:   string;
  label: string;
}

const CACHE_MS = 30_000;
let cached: { at: number; promise: Promise<ProofTypeOption[]> } | null = null;

function loadProofTypes(fresh: boolean): Promise<ProofTypeOption[]> {
  if (!fresh && cached && Date.now() - cached.at < CACHE_MS) return cached.promise;
  const promise = getJson<SystemConfig[]>("/api/config/all?category=proof_type").then((res) => {
    if (!res.ok) {
      cached = null;
      throw new Error(res.error);
    }
    return res.data
      .filter((row) => row.isActive)
      .sort((a, b) => a.sortOrder - b.sortOrder)
      .map(({ key, label }) => ({ key, label }));
  });
  cached = { at: Date.now(), promise };
  return promise;
}

export function useProofTypes(fresh = false): { options: ProofTypeOption[]; isLoading: boolean; error: string | null } {
  const [state, setState] = useState<{ options: ProofTypeOption[]; isLoading: boolean; error: string | null }>({
    options: [], isLoading: true, error: null,
  });

  useEffect(() => {
    let cancelled = false;
    loadProofTypes(fresh).then(
      (options) => { if (!cancelled) setState({ options, isLoading: false, error: null }); },
      () => { if (!cancelled) setState({ options: [], isLoading: false, error: "Unable to load proof types" }); }
    );
    return () => { cancelled = true; };
  }, [fresh]);

  return state;
}
